import { api } from 'common';
export const CreateNotableObjectDocumentationMarkdown = `
# Create Notable Object

This block creates a record in Notable's database.

## When to use this block

Use this block when the flow needs to create a record in Notable's database.

## How to use this block

Select the Notable object you want to create (see options below). Based on the object selected, the block will either display the appropriate configuration fields or you can select the data from a previous block.

### Supported objects

We support the following objects:

#### Encounter

Update an Encounter in Notable's database based on input in the configuration from a previous block.

##### Input Parameters
Encounter:

\`\`\`json
{
  "ehrInformationId": "string (required) - refers to the ID of the EHR Information entity that is created along with a patient entity by Notable",
  "ehrReferenceValue": "string (optional) - refers to the CSN, FIN, or Encounter ID of an appointment",
  "isClosed": "boolean (optional) - refers to whether or not the encounter is closed and will default to false",
  "providerUserId": "string (required) - refers to the ID of a user entity that is created along with a provider entity by Notable",
  "schedulingSource": "string (optional) - refers to the scheduling source of the encounter and will default to unknown",
  "source": "string (required) - refers to the source of the encounter",
  "startTime": "string (required) - refers to the encounter start time which must be a valid date with timestamp in ISO 8601 formats with the correct timezone",
  "appointmentStatus": "string (optional) - refers to the status of the appointment in the booking process",
  "kindOfVisit": "string (optional) - refers to the appointment type as defined by the EHR",
  "patientLocation": "string (optional) - refers to the department, physical location or organizational unit within the healthcare facility where the appointment is scheduled to take place and found in HL7 SIU message. Can be used to filter within flows",
  "ehrEncounterType": "string (optional) - refers to the clinical interaction or service delivery within the EHR system from a broader, often administrative, billing, or documentation perspective",
  "appointmentFhirKind": "string (optional) - refers to a classification or category of the appointment specifically defined using FHIR standards"
}
\`\`\`

##### Allowed values:
- schedulingSource: ${Object.values(api.Encounter.SchedulingSource).join(', ')}
- source: ${Object.values(api.Encounter.Source).join(', ')}
- appointmentStatus: ${Object.values(api.Encounter.AppointmentStatus).join(', ')}

##### How to retrieve certain values (examples):
- ehrInformationId: GetNotableObject/UpdateNotableObject block for Patients
- providerUserId: GetNotableObject/UpdateNotableObject block for Providers

#### Patient

Create a Patient in Notable's database based on input in the configuration from a previous block.

##### Input Parameters
Patient:

\`\`\`json
{
  "firstName": "string - required",
  "lastName": "string - required",
  "dateOfBirth": "string - required i.e. valid date in ISO 8601 formats -> 2000-01-01",
  "phoneNumber": "string - optional i.e. valid US phone number -> 800-456-7890",
  "cellPhoneNumber": "string - optional i.e. valid US phone number -> 800-456-7890",
  "homePhoneNumber": "string - optional i.e. valid US phone number -> 800-456-7890",
  "workPhoneNumber": "string - optional i.e. valid US phone number -> 800-456-7890",
  "email": "string - optional i.e. valid email address -> johndoe@example.com",
  "preferredLanguage": "string - optional (enum Language) i.e. ISO 639-1 Language Codes -> en for English",
  "sex": "string - optional (enum Sex)",
  "allowableContactMethods": "array of strings - optional (enum PreferredContactMethod) i.e. ['EMAIL', 'PHONE', 'TEXT']",
  "preferredContactMethod": "string - optional (enum PreferredContactMethod) i.e. PHONE, TEXT, EMAIL', or DO_NOT_CONTACT",
  "preferredPhoneNumber": "string - optional (enum PhoneNumberKind) i.e. CELL, WORK, or HOME",
  "patientEhrId": "string - required - refers to the patient's MRN",
}
\`\`\`

Allowed Enum Values:
- Sex: male, female, other, transgender, other, UNKNOWN
- PreferredContactMethod: DO_NOT_CONTACT, PHONE, TEXT, EMAIL
- PhoneNumberKind: CELL, WORK, HOME
- ContactMethod: EMAIL, PHONE, TEXT
- Language: ${Object.values(api.Language.Language).join(', ')}

##### Output

The block creates a Patient in Notable's database and returns the patient object.

##### Usage

This is necessary before creating an encounter in Notable's database.

#### ServiceReviewSubmissions

Create one or more ServiceReviewSubmissions in Notable's database based on input in the configuration from a previous block.

##### Input Parameters
- **Order Source**: The PatientOrder to create the submissions under. Must select a previous block that has an Order source.
- **ServiceReviewSubmissions Source**: The source of the ServiceReviewSubmissions to create. See below for required format.

###### ServiceReviewSubmissions Format
\`\`\`json
[
  {
        requestDate: "date - optional - Date on which the service/referral submission was requested",
        certificationIssueDate: "date - optional - Date on which the health plan authorized the service/referral",
        certificationEffectiveDate: "date - optional - Date on which the health plan authorization takes place",
        certificationExpirationDate: "date - optional - Date on which the health plan authorization expires",
        certificationNumber: "string - optional - The health plan's authorization number for claims",
        referenceNumber: "string - optional - The health plan's reference number assigned for pended or incomplete reviews",
        controlNumber: "string - optional - The health plan's last transaction ID for the review; may be used in support calls",
        traceNumbers: "array of strings - optional - Trace numbers the health plan has assigned to the review; may be used in support calls",
        payorNotes: "string - optional - The notes the health plan added to the review",
        payorX12StatusCode: "string - optional (enum PayorX12StatusCode) - The X12 standardized service code for health plan's current status",
        payorStatusMetadata: "object - optional - A health plan's additional metadata for the status"
        verificationFileReference: "string - optional - File reference to a verification document in Google Storage; screenshots and videos",
        lifeCycleStatus: "string - optional - An optional indicator of the state of the submission in the Notable platform workflow",
        lifeCycleStatusMetadata: "object - optional - Metadata for lifeCycle status",
        payorPortal: "string - optional (enum ServiceReviewPayorPortal) - The portal submitted against",
        submissionMethod: "string - optional (enum ServiceReviewSubmissionMethod) - The submission method"
  }
]
\`\`\`

Allowed Enum Values:
- PayorX12StatusCode: A1, A2, A3, A4, A6, C, CT, NA, 51, 71
- ServiceReviewPayorPortal: AIM, AVAILITY, BCBSMS_MEDICAL_NECESSITY, CARELON, CARE_CORE, COHERE, EVICORE, HEALTH_HELP, MEDSOLUTIONS, MHK, RADMD, UNITED_HEALTHCARE, FAX, EHR
- ServiceReviewSubmissionMethod: ANALYST, API, HUMAN_TASK, RPA


##### Output

The block creates one or more ServiceReviewSubmissions in Notable's database and returns the created objects.

#### AuthorizationSubmissions

Create one or more AuthorizationSubmissions in Notable's database based on input in the configuration from a previous block.

##### Input Parameters
- **Order Source**: The PatientOrder to create the submissions under. Must select a previous block that has an Order source.
- **AuthorizationSubmissions Source**: The source of the AuthorizationSubmissions to create. See below for required format.

###### AuthorizationSubmissions Format
\`\`\`json
[
  {
        payorPortal: "string - optional (enum PayorPortal) - The portal submitted against",
        requestedDate: "date - optional - Date on which the authorization submission was requested",
        obtainedDate: "date - optional - Date on which the health plan authorized the service/referral",
        effectiveDate: "date - optional - Date on which the health plan authorization takes place",
        expirationDate: "date - optional - Date on which the health plan authorization expires",
        trackingNumber: "string - optional",
        authorizationNumber: "string - optional",
        notRequiredReason: "string - optional",
        notRequiredComment: "string - optional",
        exceptionReason: "string - optional",
        exceptionComment: "string - optional",
        pendingReason: "string - optional",
        pendingComment: "string - optional",
        pendingReason: "string - optional",
        pendingComment: "string - optional",
        submissionMethod: "string - optional (enum SubmissionMethod) - The submission method",
        status: "string - optional (enum SubmissionStatus) - the status of the submission, normalized",
        rawStatus: "string - optional - the status of the submission from the EHR",
        verificationFileReference: "string - optional - File reference to a verification document in Google Storage; screenshots and videos",
        procedureIds: array of strings - IDs of Procedures from the associated Order that are associated with this request",
  }
]
\`\`\`

Allowed Enum Values:
- PayorPortal: AIM, AVAILITY, BCBSMS_MEDICAL_NECESSITY, CARELON, Core = CARE_CORE, COHERE, EVICORE, HEALTH_HELP, MEDSOLUTIONS, MHK, PNT_DATA, RADMD, UNITED_HEALTHCARE
- SubmissionMethod: ANALYST, API, HUMAN_TASK, RPA
- SubmissionStatus: APPROVED, REJECTED, EXCEPTION, NOT_REQUIRED, PENDING, UNKNOWN


##### Output

The block creates one or more AuthorizationSubmissions in Notable's database and returns the created objects.
`;
